import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Input } from "@/components/ui/input";
import { Scale, ArrowRight, FileText, Search, CheckCircle2, Circle, LayoutGrid, Rows3 } from "lucide-react";
import { useDragSelect } from "@/lib/useDragSelect";
import { setQueue } from "@/lib/practiceQueue";
import SelectionTray from "@/components/SelectionTray";

const DIFFS = [
  { k: "all", label: "All" },
  { k: "easy", label: "Easy" },
  { k: "medium", label: "Medium" },
  { k: "hard", label: "Hard" },
];

const formatDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return "—"; }
};

const badgeClass = (d) => ({
  easy:   "bg-emerald-50 text-emerald-700 border-emerald-300",
  medium: "bg-sky-50 text-sky-700 border-sky-300",
  hard:   "bg-rose-50 text-rose-700 border-rose-300",
}[d] || "bg-slate-100 text-slate-700 border-slate-300");

/**
 * Legal passage list — structured, exam-catalogue style so students can see at
 * a glance which exercises are pending, which they've attempted, and their
 * best accuracy. Renders as either a data-dense table (default) or a card
 * grid via the top-right toggle. Real-time — the moment an admin publishes a
 * new passage it appears on refresh (or immediately in same-tab reload).
 */
export default function LegalList() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({}); // { [item_id]: {attempts, best_accuracy, best_wpm} }
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [diff, setDiff] = useState("all");
  const [view, setView] = useState(() => localStorage.getItem("stenosync.legal.view") || "table");
  const listRef = useRef(null);
  const { selected, dragging, rect, toggle, clear } = useDragSelect(listRef);

  useEffect(() => {
    Promise.all([
      api.get("/legal-passages"),
      api.get("/attempts/summary?module=legal").catch(() => ({ data: {} })),
    ]).then(([lp, sm]) => {
      setItems(lp.data || []);
      setSummary(sm.data || {});
    }).finally(() => setLoading(false));
  }, []);

  const changeView = (v) => { setView(v); try { localStorage.setItem("stenosync.legal.view", v); } catch {} };

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return items.filter((p) => {
      if (diff !== "all" && p.difficulty !== diff) return false;
      if (query && !p.title.toLowerCase().includes(query) && !(p.court || "").toLowerCase().includes(query)) return false;
      return true;
    });
  }, [items, q, diff]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 py-12">
        <div className="mb-8">
          <div className="text-xs uppercase tracking-[0.3em] font-bold text-brand-gold mb-3">Module 02</div>
          <h1 className="text-4xl lg:text-5xl font-black tracking-tight flex items-center gap-4" style={{color: "var(--brand-navy)"}}>
            <Scale className="w-9 h-9" /> Legal Formatting Typing
          </h1>
          <p className="mt-3 font-mono text-muted-foreground max-w-2xl">
            Real judgements from High Courts and the Supreme Court. Track your best accuracy and WPM per exercise.
          </p>
        </div>

        {/* Toolbar row — sticky so search stays on screen */}
        <div className="sticky top-16 z-30 -mx-6 px-6 py-3 bg-background/95 backdrop-blur border-b border-border/60 mb-6 flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[260px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground/70 pointer-events-none" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search title or court…"
              className="rounded-lg h-11 pl-9 font-mono border-border bg-card"
              data-testid="legal-search"
            />
          </div>
          <div className="inline-flex border border-border rounded-lg overflow-hidden" role="tablist" aria-label="Difficulty filter">
            {DIFFS.map((d, i) => (
              <button
                key={d.k}
                onClick={() => setDiff(d.k)}
                data-testid={`legal-diff-${d.k}`}
                className={`px-4 py-2 text-[10px] uppercase tracking-[0.2em] font-bold ${i > 0 ? "border-l border-border" : ""} transition-colors ${
                  diff === d.k ? "bg-brand-gold text-white" : "bg-card text-muted-foreground hover:bg-secondary"
                }`}
              >{d.label}</button>
            ))}
          </div>
          <div className="inline-flex border border-border rounded-lg overflow-hidden" role="tablist" aria-label="View mode">
            <button
              type="button"
              onClick={() => changeView("table")}
              data-testid="legal-view-table"
              className={`px-3 py-2 flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold transition-colors ${view === "table" ? "bg-brand-gold text-white" : "bg-card text-muted-foreground hover:bg-secondary"}`}
              title="Table view"
            ><Rows3 className="w-3.5 h-3.5" /> Table</button>
            <button
              type="button"
              onClick={() => changeView("cards")}
              data-testid="legal-view-cards"
              className={`px-3 py-2 flex items-center gap-1.5 border-l border-border text-[10px] uppercase tracking-widest font-bold transition-colors ${view === "cards" ? "bg-brand-gold text-white" : "bg-card text-muted-foreground hover:bg-secondary"}`}
              title="Card view"
            ><LayoutGrid className="w-3.5 h-3.5" /> Cards</button>
          </div>
          <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground hidden md:block">
            Tip · Drag to select multiple
          </div>
        </div>

        <div ref={listRef} className="relative select-none" data-testid="legal-list-region">
          {loading ? (
            <div className="font-mono text-muted-foreground">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="border-2 border-dashed border-border p-10 text-center font-mono text-muted-foreground rounded-xl bg-card" data-testid="legal-empty">
              {items.length === 0 ? "No passages yet." : "No passages match your filters."}
            </div>
          ) : view === "table" ? (
            <LegalTable items={filtered} summary={summary} selected={selected} onToggle={toggle} />
          ) : (
            <LegalCards items={filtered} summary={summary} selected={selected} onToggle={toggle} />
          )}

          {dragging && rect && (
            <div
              className="fixed pointer-events-none border-2 border-brand-gold bg-brand-gold/10 rounded"
              style={{ left: rect.left, top: rect.top, width: rect.right - rect.left, height: rect.bottom - rect.top }}
              data-testid="drag-marquee"
            />
          )}
        </div>

        <SelectionTray
          count={selected.size}
          onClear={clear}
          onStart={() => setQueue("legal", Array.from(selected))}
          firstHref={selected.size ? `/legal/${Array.from(selected)[0]}` : "#"}
          moduleLabel="Legal"
        />
      </main>
    </div>
  );
}

/** Data-dense exam catalogue with Ex #, title, category, words, upload date, status, action. */
function LegalTable({ items, summary, selected, onToggle }) {
  return (
    <div className="border border-border rounded-xl overflow-hidden bg-card shadow-sm" data-testid="legal-table-wrap">
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="legal-table">
          <thead className="bg-secondary/60 border-b border-border">
            <tr className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
              <th className="px-3 py-3 text-left w-10"></th>
              <th className="px-4 py-3 text-left w-14">#</th>
              <th className="px-4 py-3 text-left">Case title</th>
              <th className="px-4 py-3 text-left w-40">Court</th>
              <th className="px-4 py-3 text-left w-24">Difficulty</th>
              <th className="px-4 py-3 text-right w-20">Words</th>
              <th className="px-4 py-3 text-left w-28">Uploaded</th>
              <th className="px-4 py-3 text-left w-40">Status</th>
              <th className="px-4 py-3 text-right w-32">Action</th>
            </tr>
          </thead>
          <tbody>
            {items.map((p, idx) => {
              const s = summary[p.id];
              const attempted = s && s.attempts > 0;
              const isSel = selected?.has(p.id);
              return (
                <tr
                  key={p.id}
                  data-testid={`legal-row-${p.id}`}
                  data-select-key={p.id}
                  className={`border-b border-border last:border-b-0 transition-colors cursor-pointer ${isSel ? "bg-brand-gold/15" : "hover:bg-secondary/40"}`}
                >
                  <td className="px-3 py-3 align-top" data-noselect>
                    <input
                      type="checkbox"
                      checked={!!isSel}
                      onChange={() => onToggle?.(p.id)}
                      onClick={(e) => e.stopPropagation()}
                      className="w-4 h-4 accent-[var(--brand-gold)] cursor-pointer"
                      data-testid={`legal-check-${p.id}`}
                      aria-label={`Select ${p.title}`}
                    />
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-muted-foreground align-top">{String(idx + 1).padStart(2, "0")}</td>
                  <td className="px-4 py-3 align-top">
                    <div className="font-bold text-foreground leading-tight">{p.title}</div>
                    {p.content && (
                      <div className="mt-1 text-xs font-mono text-muted-foreground/80 line-clamp-1">
                        {p.content.slice(0, 140)}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 align-top text-xs font-mono text-muted-foreground">{p.court || "—"}</td>
                  <td className="px-4 py-3 align-top">
                    <span className={`inline-block text-[10px] font-mono px-2 py-0.5 border rounded-md ${badgeClass(p.difficulty)}`}>
                      {(p.difficulty || "medium").toUpperCase()}
                    </span>
                  </td>
                  <td className="px-4 py-3 align-top text-right font-mono text-sm">{p.word_count || 0}</td>
                  <td className="px-4 py-3 align-top text-xs font-mono text-muted-foreground">{formatDate(p.created_at)}</td>
                  <td className="px-4 py-3 align-top">
                    <StatusPill attempted={attempted} accuracy={s?.best_accuracy} wpm={s?.best_wpm} attempts={s?.attempts} />
                  </td>
                  <td className="px-4 py-3 align-top text-right">
                    <Link
                      to={`/legal/${p.id}`}
                      data-testid={`legal-open-${p.id}`}
                      className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg bg-brand-gold hover:bg-[var(--brand-gold-hover)] text-white text-[11px] uppercase tracking-widest font-bold transition-colors"
                    >
                      {attempted ? "Retry" : "Start test"} <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function LegalCards({ items, summary, selected, onToggle }) {
  return (
    <div className="grid md:grid-cols-2 gap-4" data-testid="legal-list">
      {items.map((p, idx) => {
        const s = summary[p.id];
        const attempted = s && s.attempts > 0;
        const isSel = selected?.has(p.id);
        return (
          <div
            key={p.id}
            data-testid={`legal-item-${p.id}`}
            data-select-key={p.id}
            className={`group relative border p-6 flex flex-col rounded-xl transition-all cursor-pointer ${
              isSel ? "border-brand-gold ring-2 ring-brand-gold/40 bg-brand-gold/5" : "border-border bg-card hover:border-brand-gold/60 hover:-translate-y-1 hover:shadow-md"
            }`}
          >
            <input
              type="checkbox"
              checked={!!isSel}
              onChange={() => onToggle?.(p.id)}
              onClick={(e) => e.stopPropagation()}
              data-noselect
              data-testid={`legal-check-${p.id}`}
              aria-label={`Select ${p.title}`}
              className="absolute top-3 left-3 w-4 h-4 accent-[var(--brand-gold)] cursor-pointer z-10"
            />
            <div className="flex items-start justify-between mb-3 gap-3">
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-muted-foreground/70">
                  Exercise #{String(idx + 1).padStart(2, "0")} · {p.court}
                </div>
                <h3 className="text-lg font-black tracking-tight mt-1 leading-tight" style={{color: "var(--brand-navy)"}}>{p.title}</h3>
              </div>
              <span className={`shrink-0 text-[10px] font-mono px-2 py-0.5 border rounded-md ${badgeClass(p.difficulty)}`}>
                {(p.difficulty || "medium").toUpperCase()}
              </span>
            </div>
            <div className="text-sm font-mono text-muted-foreground line-clamp-2 flex-1">{p.content}</div>
            <div className="flex items-center justify-between mt-4 gap-3 flex-wrap">
              <span className="text-[11px] uppercase tracking-widest font-bold text-muted-foreground">
                <FileText className="w-3.5 h-3.5 inline mr-1" /> {p.word_count} words · {formatDate(p.created_at)}
              </span>
              <StatusPill attempted={attempted} accuracy={s?.best_accuracy} wpm={s?.best_wpm} attempts={s?.attempts} />
            </div>
            <div className="mt-4 text-[11px] uppercase tracking-widest font-bold text-brand-gold group-hover:underline underline-offset-4">
              <Link to={`/legal/${p.id}`} onClick={(e) => e.stopPropagation()} data-testid={`legal-open-${p.id}`}>
                {attempted ? "Retry test" : "Start test"} <ArrowRight className="w-3.5 h-3.5 inline" />
              </Link>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function StatusPill({ attempted, accuracy, wpm, attempts }) {
  if (!attempted) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] font-mono uppercase tracking-widest font-bold bg-slate-100 text-slate-600 border border-slate-300">
        <Circle className="w-3 h-3" /> Pending
      </span>
    );
  }
  const tone =
    accuracy >= 90 ? "bg-emerald-50 text-emerald-700 border-emerald-300" :
    accuracy >= 70 ? "bg-sky-50 text-sky-700 border-sky-300" :
    "bg-rose-50 text-rose-700 border-rose-300";
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] font-mono uppercase tracking-widest font-bold border ${tone}`} title={`${attempts} attempt(s)`}>
      <CheckCircle2 className="w-3 h-3" /> {accuracy}% · {wpm} WPM
    </span>
  );
}
